import React from 'react'
import { func, object, shape } from 'prop-types'
import { Switch, Route, withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import track from 'react-tracking'
import queryString, { parse } from 'query-string'
import BEMHelper from '@ynap/bem-helper'
import { raygun } from '@ynap/clientside-monitoring'
import Spinner from '@ynap/spinner'
import { ImageWithChild } from '@ynap/image'
import { Button } from '@ynap/button'
import { dispatch } from '@ynap/analytics'
import { ErrorBoundary } from '@ynap/error-page'
import { authActions } from '@ynap/auth-state'
import { countryActions } from '@ynap/country-state'
import { contentActions } from '@ynap/content-state'
import { userActions } from '@ynap/user-state'
import getUserSummary from 'app/state/myaccount-state/actions/getUserSummary'
import getMultipleLanguageCountries from 'app/state/preferences-state/selectors/getMultipleLanguageCountries'

import LoginAndRegistrationForm, { Panel } from '@ynap/login-and-registration-form'

import {
    ForgotPassword,
    MarketingPreferences,
    getConfig,
    OrderDetails,
    Referred,
    GuestOrders,
    Unsubscribe,
    DecoratedConsentPhone,
} from 'app/components'
import { getApp, getSpinner, getServiceMessage, getLinks } from 'app/state/myaccount-state/selectors'
import { Account } from 'app/containers'
import cookieExists, { tryGetLoginModeFromRequest } from 'app/containers/App/utils'
import { orderActions, getOrderDetails } from 'app/state/order-history-state'
import { ScrollToTopOnMount as ScrollToTop } from '@ynap/scroll-to-top'
import ImmutablesProvider from '@ynap/immutables-provider'
import { OptimizelyProvider, OptimizelyFeature } from '@ynap/optimizely-utils'
import pushDataToLiveChat from '../../utils/live-chat'
import ServiceMessage from 'app/components/ServiceMessage/ServiceMessage'
import { preferencesActions, getPreferences } from 'app/state/preferences-state'
import getCountries from 'app/state/preferences-state/selectors/getCountries'

const className = new BEMHelper('App')

const SpinnerContainer = connect(getSpinner)(Spinner)

const LoginAndRegistrationContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getMultipleLanguageCountries(state),
    }),
    { ...authActions, ...countryActions }
)(LoginAndRegistrationForm)

const ForgotPasswordContainer = connect(getConfig, authActions)(ForgotPassword)

const OrderDetailsContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getOrderDetails(state, props),
        links: getLinks(state, props),
    }),
    { ...orderActions, push, ...countryActions, fetchContent: contentActions.fetchContent, ...countryActions }
)(OrderDetails)

const MarketingPreferencesContainer = connect(
    (state) => ({
        ...getCountries(state),
        ...getPreferences(state),
    }),
    {
        ...contentActions,
        ...preferencesActions,
        ...countryActions,
        ...userActions,
    }
)(MarketingPreferences)

const GuestOrdersContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getOrderDetails(state, props),
    }),
    { ...orderActions, push, ...countryActions, fetchContent: contentActions.fetchContent, ...countryActions }
)(GuestOrders)

const UnsubscribeContainer = connect(getConfig, { ...userActions })(Unsubscribe)

const ServiceMessageContainer = connect(
    (state, props) => ({
        ...getConfig(state, props),
        ...getServiceMessage(state, props),
    }),
    {
        fetchContent: contentActions.fetchContent,
    }
)(ServiceMessage)

@track(
    {
        category: {
            pageType: 'account management',
            primaryCategory: 'my account',
        },
    },
    {
        dispatch: ({ lpTag, ...gtmData } = {}) => {
            dispatch(gtmData)
            pushDataToLiveChat(gtmData, lpTag)
        },
    }
)
class App extends React.Component {
    static propTypes = {
        messages: shape({
            loginAndRegistrationForm: shape({
                title: func.isRequired,
                subTitle: func.isRequired,
            }).isRequired,
        }).isRequired,
        locale: object,
        location: object,
        pages: object,
        config: object,
    }

    constructor(props) {
        super(props)

        this.state = {
            rowLayout: null,
            userSummary: null,
        }
    }

    componentDidUpdate() {
        const { iframe } = queryString.parse(this.props.location.search)
        if (iframe === 'true') {
            const height = document.body.scrollHeight
            if (height > 0) {
                window.parent.postMessage({ type: 'zizrModalHeight', height: height }, '*')
                window.parent.postMessage({ type: 'accountModalHeight', height: height }, '*')
            }
        }
    }

    componentDidMount() {
        const { config, locale } = this.props
        const raygunConfig = config.head.raygun

        if (raygunConfig.enabled && raygunConfig.apiKey) {
            raygun.agent.init(raygunConfig)
        }

        if (!this.state.rowLayout) {
            this.setState({ rowLayout: 'false' })
        }

        getUserSummary(locale, false).then((userSummary) => {
            this.setState({ userSummary })
        })
    }

    render() {
        const { messages, locale, location, config, pages = {}, optimizely, ssr = false, ...rest } = this.props
        const { emailPreferences, guestorderdetails, orderTracking, returnsTracking, createGuestReturn, unsubscribe } =
            pages
        const params = queryString.parse(location.search)
        const { email, newsletterFullRegister, iframe } = params

        const nativeAppCookies = ['nativeapp', 'CLIENTINFO']
        const nativeApp = cookieExists(nativeAppCookies) || iframe === 'true'

        const {
            flags,
            backgroundImage,
            affiliateBackgroundImage,
            emptyLink,
            links,
            buyapowaCampaign,
            buyapowaURL,
            buyapowaMarket,
            consentPhone,
            gdprLikeCountries,
            ageConsentCountries,
            tcConsentCountries,
            noUppercaseLangs,
        } = config?.head
        const {
            enableTitles = true,
            enableAlternativeMarketing = true,
            sizeGridFeatureToggle,
            isGuestOrdersTrackingEnabled = false,
            enableServiceMessageCoremediaContent,
            isCoremediaV2,
        } = flags
        const { accessKey, email: emailLocation } = typeof window !== 'undefined' ? parse(window?.location.search) : {}
        const hideNav = !!(accessKey && emailLocation)

        const rpiRequired = flags?.rpiRequiredCountryList?.includes(locale?.country)

        return (
            <ImmutablesProvider value={{ messages, config }}>
                <ErrorBoundary messages={messages}>
                    <OptimizelyProvider optimizely={optimizely} isServerSide={ssr}>
                        <SpinnerContainer className={className()}>
                            <Switch location={location}>
                                <Route
                                    path="/:locale/account/:signin(login|register)"
                                    exact
                                    render={(routeProps) => {
                                        const { match, history, location } = routeProps
                                        const { search } = location
                                        const { signin } = match.params
                                        const additionalText = tryGetLoginModeFromRequest(params, messages)
                                        const hasAccount = String(signin === 'login')
                                        const gdprConsent = !!gdprLikeCountries?.includes(locale?.country)
                                        const ageConsent =
                                            !!ageConsentCountries?.includes(locale?.country) &&
                                            messages.registrationForm.ageConsent?.()
                                        const tcConsent =
                                            !!tcConsentCountries?.includes(locale?.country) &&
                                            messages.registrationForm.tcConsent?.()

                                        const { enableMarketingOptInCountryList } = flags || {}

                                        const optInRolloutInCountry = enableMarketingOptInCountryList?.includes(
                                            locale?.country
                                        )
                                        const initialMarketingConsent = optInRolloutInCountry?.toString()

                                        return (
                                            <OptimizelyFeature
                                                feature={[
                                                    'WEB00599_registerloginmessage_signin_web',
                                                    'WEB00600_registerloginmessage_signin_mob',
                                                    'WEB00601_registerloginmessage_signin_web',
                                                    'WEB00602_registerloginmessage_signin_mob',
                                                ]}
                                                {...this.props}
                                            >
                                                {(props) => {
                                                    const { variables = {} } = props
                                                    const { bannerVariation } = variables
                                                    const isVariation =
                                                        bannerVariation === 'v1' || bannerVariation === 'v2'
                                                    const rowLayout =
                                                        hasAccount === 'true' && isVariation ? 'true' : 'false'

                                                    if (bannerVariation && this.state.rowLayout !== rowLayout) {
                                                        this.setState({
                                                            rowLayout,
                                                        })
                                                    }

                                                    return (
                                                        <Panel
                                                            className={className('title')}
                                                            title={
                                                                this.state.rowLayout &&
                                                                (hasAccount === 'true'
                                                                    ? messages.loginAndRegistrationForm.hasAccountTitle()
                                                                    : messages.loginAndRegistrationForm.noAccountTitle())
                                                            }
                                                            subTitle={
                                                                this.state.rowLayout === 'true' &&
                                                                messages.loginAndRegistrationForm.loginSubtitle?.()
                                                            }
                                                            additionalInfo={additionalText}
                                                            rowLayout={this.state.rowLayout === 'true' && 'rowLayout'}
                                                        >
                                                            {this.state.rowLayout ? (
                                                                <LoginAndRegistrationContainer
                                                                    optimizely={optimizely}
                                                                    messages={messages}
                                                                    config={config}
                                                                    locale={locale}
                                                                    initialValues={{
                                                                        hasAccount,
                                                                        ...(email && { logonId: email }),
                                                                        registration: {
                                                                            maMarketingConsent: initialMarketingConsent,
                                                                            ...(flags?.enableGenderSelection && {
                                                                                gender: 'Unspecified',
                                                                            }),
                                                                            gdprConsent: gdprConsent
                                                                                ? 'false'
                                                                                : undefined,
                                                                        },
                                                                    }}
                                                                    gdprConsent={gdprConsent}
                                                                    ageConsent={ageConsent}
                                                                    tcConsent={tcConsent}
                                                                    noUppercaseLangs={noUppercaseLangs}
                                                                    enableTitles={enableTitles}
                                                                    enableAlternativeMarketing={
                                                                        enableAlternativeMarketing
                                                                    }
                                                                    getLinks={getLinks}
                                                                    onRegisteredUserChanged={(isRegisteredUser) => {
                                                                        const pathname = `/${locale.id}/account/${
                                                                            isRegisteredUser ? 'login' : 'register'
                                                                        }`
                                                                        history.replace({
                                                                            pathname,
                                                                            search,
                                                                        })
                                                                    }}
                                                                    newsletterFullRegister={newsletterFullRegister}
                                                                    {...rest}
                                                                    bannerVariation={bannerVariation}
                                                                />
                                                            ) : null}
                                                        </Panel>
                                                    )
                                                }}
                                            </OptimizelyFeature>
                                        )
                                    }}
                                />
                                <Route
                                    path="/:locale/account/centurion"
                                    exact
                                    render={() => {
                                        const lowQualitySrc = affiliateBackgroundImage?.lowQualitySrc || ' '
                                        const highQualitySrc = affiliateBackgroundImage?.highQualitySrc || ' '
                                        const hasAccount = String(!!email)

                                        return (
                                            <ImageWithChild
                                                className={className('background')}
                                                aspectRatio={'background'}
                                                lowQualitySrc={lowQualitySrc}
                                                src={highQualitySrc}
                                                cover={true}
                                            >
                                                <Panel
                                                    className={className('title')}
                                                    title={messages.affiliateLoginAndRegistrationForm.title()}
                                                    subTitle={messages.affiliateLoginAndRegistrationForm.subTitle()}
                                                >
                                                    <LoginAndRegistrationContainer
                                                        optimizely={optimizely}
                                                        messages={messages}
                                                        config={config}
                                                        locale={locale}
                                                        initialValues={{
                                                            hasAccount,
                                                            email,
                                                            registration: {
                                                                maMarketingConsent: messages.registrationForm
                                                                    .marketingError
                                                                    ? null
                                                                    : 'false',
                                                                ...(flags?.enableGenderSelection && {
                                                                    gender: 'Female',
                                                                }),
                                                            },
                                                        }}
                                                        enableTitles={enableTitles}
                                                        enableAlternativeMarketing={enableAlternativeMarketing}
                                                        affiliate={true}
                                                        getLinks={getLinks}
                                                        params={params}
                                                        {...rest}
                                                    />
                                                </Panel>
                                            </ImageWithChild>
                                        )
                                    }}
                                />
                                <Route
                                    path="/:locale/account/affiliate-confirmation"
                                    exact
                                    render={() => {
                                        const lowQualitySrc = affiliateBackgroundImage?.lowQualitySrc || ' '
                                        const highQualitySrc = affiliateBackgroundImage?.highQualitySrc || ' '

                                        return (
                                            <ImageWithChild
                                                className={className('background')}
                                                aspectRatio={'background'}
                                                lowQualitySrc={lowQualitySrc}
                                                src={highQualitySrc}
                                                cover={true}
                                            >
                                                <Panel
                                                    className={className('title')}
                                                    title={messages.affiliateConfirmation.title()}
                                                    subTitle={messages.affiliateConfirmation.subTitle()}
                                                >
                                                    <Button
                                                        type="primary"
                                                        component="a"
                                                        href={`/${locale.id}${emptyLink}`}
                                                    >
                                                        {messages.affiliateConfirmation.link()}
                                                    </Button>
                                                </Panel>
                                            </ImageWithChild>
                                        )
                                    }}
                                />
                                <Route
                                    path="/:locale/account/forgot-password"
                                    render={() => {
                                        const lowQualitySrc = backgroundImage?.lowQualitySrc || ' '
                                        const highQualitySrc = backgroundImage?.highQualitySrc || ' '

                                        return (
                                            <ImageWithChild
                                                className={className('background')}
                                                aspectRatio={'background'}
                                                lowQualitySrc={lowQualitySrc}
                                                src={highQualitySrc}
                                                cover={true}
                                                noLogo={true}
                                            >
                                                <ForgotPasswordContainer
                                                    messages={messages}
                                                    config={config}
                                                    locale={locale}
                                                    getLinks={getLinks}
                                                    noUppercaseLangs={noUppercaseLangs}
                                                />
                                            </ImageWithChild>
                                        )
                                    }}
                                />
                                {emailPreferences ? (
                                    <Route
                                        path={`/:locale${emailPreferences.to}`}
                                        render={() => (
                                            <>
                                                <ScrollToTop>
                                                    <MarketingPreferencesContainer
                                                        messages={messages}
                                                        config={config}
                                                        getLinks={getLinks}
                                                        nativeApp={nativeApp}
                                                        accessKey={accessKey}
                                                        email={emailLocation}
                                                        isCoremediaV2={isCoremediaV2}
                                                        userSummary={this.state.userSummary}
                                                    />
                                                </ScrollToTop>
                                                {!(nativeApp || hideNav) ? (
                                                    <ServiceMessageContainer
                                                        enableEditorialContent={enableServiceMessageCoremediaContent}
                                                        messages={messages}
                                                        links={links}
                                                        locale={locale}
                                                        isCoremediaV2={isCoremediaV2}
                                                    />
                                                ) : null}
                                            </>
                                        )}
                                    />
                                ) : null}
                                {guestorderdetails ? (
                                    <Route
                                        path={`/:locale${guestorderdetails.to}`}
                                        render={({ match: { params }, location }) => (
                                            <>
                                                <ScrollToTop>
                                                    <OrderDetailsContainer
                                                        messages={messages}
                                                        location={location}
                                                        externalOrderId={params.externalOrderId}
                                                        sizeGridFeatureToggle={sizeGridFeatureToggle}
                                                        guestUser={true}
                                                        isCoremediaV2={isCoremediaV2}
                                                        {...rest}
                                                    />
                                                </ScrollToTop>
                                                <ServiceMessageContainer
                                                    enableEditorialContent={enableServiceMessageCoremediaContent}
                                                    messages={messages}
                                                    links={links}
                                                    locale={locale}
                                                    isCoremediaV2={isCoremediaV2}
                                                />
                                            </>
                                        )}
                                    />
                                ) : null}
                                {orderTracking && isGuestOrdersTrackingEnabled ? (
                                    <Route
                                        path={`/:locale${orderTracking.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <GuestOrdersContainer
                                                    type={`trackOrder`}
                                                    nativeApp={nativeApp}
                                                    messages={messages}
                                                />
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {returnsTracking && isGuestOrdersTrackingEnabled ? (
                                    <Route
                                        path={`/:locale${returnsTracking.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <GuestOrdersContainer
                                                    type={'trackReturn'}
                                                    nativeApp={nativeApp}
                                                    messages={messages}
                                                />
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {createGuestReturn && isGuestOrdersTrackingEnabled ? (
                                    <Route
                                        path={`/:locale${createGuestReturn.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <GuestOrdersContainer
                                                    type={'createReturn'}
                                                    nativeApp={nativeApp}
                                                    messages={messages}
                                                    rpiRequired={rpiRequired}
                                                />
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                {unsubscribe ? (
                                    <Route
                                        path={`/:locale${unsubscribe.to}`}
                                        render={() => (
                                            <ScrollToTop>
                                                <UnsubscribeContainer messages={messages} />
                                            </ScrollToTop>
                                        )}
                                    />
                                ) : null}
                                <Route
                                    path="/:locale/account/referred"
                                    render={({ match, location }) => (
                                        <ScrollToTop>
                                            <Referred
                                                country={locale.country}
                                                messages={messages}
                                                language={locale.language}
                                                buyapowaCampaign={buyapowaCampaign}
                                                buyapowaURL={buyapowaURL}
                                                buyapowaMarket={buyapowaMarket}
                                                rootElem="bp_div"
                                                errorElem="bp_error"
                                                query={location?.query}
                                                {...rest}
                                            />
                                        </ScrollToTop>
                                    )}
                                />
                                {consentPhone ? (
                                    <Route
                                        path="/:locale/account/consentphone"
                                        render={() => (
                                            <DecoratedConsentPhone locale={locale} messages={messages} {...rest} />
                                        )}
                                    />
                                ) : null}

                                <Route
                                    render={() => (
                                        <Account
                                            messages={messages}
                                            config={config}
                                            nativeApp={nativeApp}
                                            noUppercaseLangs={noUppercaseLangs}
                                            {...rest}
                                        />
                                    )}
                                />
                            </Switch>
                        </SpinnerContainer>
                    </OptimizelyProvider>
                </ErrorBoundary>
            </ImmutablesProvider>
        )
    }
}

export default withRouter(connect(getApp)(App))
